<template>
  <div>
    <!-- Cards de Estatísticas -->
    <h5 class="mb-2">Estatísticas Afiliados</h5>
    <b-row class="mb-3">
      <b-col md="3" sm="6" v-for="(stat, index) in dataStaticsFixos" :key="'stat-afil-'+index">
        <b-card class="text-center shadow-sm border-0 h-80 card-clickable" @click="navigateToPedidos(stat)">
          <div class="d-flex align-items-center">
            <div class="mr-3">
              <div :class="`rounded-circle p-2 bg-light-${stat.color}`">
                <feather-icon :icon="stat.icon" :class="`text-${stat.color}`" size="20"/>
              </div>
            </div>
            <div>
              <p class="card-text small text-muted mb-0">{{ stat.title }}</p>
              <h5 class="font-weight-bolder mb-0">{{ stat.value }}</h5>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>
    <!-- Gráficos -->
    <b-row class="mt-1">
      <b-col lg="6" sm="12">
        <b-card title="Vendas Mensais" class="h-80">
          <bar-chart :chart-data="chartDataVendas" :options="chartOptionsVendas" :height="300" />
        </b-card>
      </b-col>
      <b-col lg="6" sm="12">
        <b-card title="Pedidos na Semana" class="h-80">
          <line-chart :chart-data="chartDataPedidos" :options="chartOptionsPedidos" :height="300" />
        </b-card>
      </b-col>
    </b-row>

    <!-- Últimos Pedidos e Resumo Rápido -->
    <b-row class="mt-1">
      <b-col lg="8" sm="12">
        <b-card title="Últimos Pedidos" class="h-100">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <h5 class="mb-0">Últimos Pedidos</h5>
            <b-link href="/pedidos" class="small text-primary">Ver todos</b-link>
          </div>
          <b-table
            :items="ultimosPedidos"
            :fields="camposTabela"
            responsive
            show-empty
            empty-text="Nenhum pedido encontrado"
            empty-filtered-text="Nenhum pedido encontrado"
          >
            <!-- Slot para formatar a coluna 'status' -->
            <template #cell(status)="data">
              <!-- Certifique-se que está acessando data.item.status.text e data.item.status.variant -->
              <b-badge :variant="data.item.status.variant" pill>
                {{ data.item.status.text }}
              </b-badge>
            </template>

            <!-- Slot para o estado vazio -->
            <template #empty>
              <div class="text-center py-5">
                <feather-icon icon="ShoppingCartIcon" size="32" class="text-muted mb-2" />
                <h6>Nenhum pedido encontrado</h6>
                <p class="small text-muted">Seus pedidos aparecerão aqui quando você começar a vender.</p>
              </div>
            </template>
          </b-table>
        </b-card>
      </b-col>
      <b-col lg="4" sm="12">
        <b-card title="Resumo Rápido" class="h-100">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <h5 class="mb-0">Resumo Rápido</h5>
          </div>
          <div class="mt-2">
            <div v-for="(item, index) in resumoRapido" :key="index" class="d-flex align-items-center p-1 mb-2">
              <div :class="`bg-light-${item.color} rounded-circle p-2 mr-2`">
                <feather-icon :icon="item.icon" size="18" :class="`text-${item.color}`" />
              </div>
              <div class="flex-grow-1">
                <span class="text-muted small">{{ item.label }}</span>
                <h6 class="mb-0">{{ item.value }}</h6>
              </div>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Dicas de Crescimento -->
    <b-card title="Dicas de Crescimento" class="mt-1">
      <b-row>
        <b-col md="4" sm="12" v-for="(dica, index) in dicasCrescimento" :key="index" class="mb-2">
          <b-card
            bg-variant="light"
            :class="`border-0 bg-light-${dica.color}`"
            class="h-100"
          >
            <div class="d-flex mb-2">
              <div :class="`bg-light-${dica.color} rounded-circle p-2 mr-2`">
                <feather-icon :icon="dica.icon" class="text-white" size="20" />
              </div>
            </div>
            <h6>{{ dica.title }}</h6>
            <p class="small text-muted mb-2">{{ dica.text }}</p>
            <b-link :href="dica.link" class="small font-weight-bold">{{ dica.linkText }}</b-link>
          </b-card>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import StaticsCard from '@/components/deashboard/StaticsCard.vue'
import { mapGetters } from 'vuex'
import {
  BCard, BRow, BCol, BTable, BLink, BBadge, BButton,
} from 'bootstrap-vue'
import { Bar, Line } from 'vue-chartjs'
import useDashboardVendedor from '@/composables/useDashboardVendedor'

// Componente de gráfico de barras personalizado
const BarChart = {
  extends: Bar,
  props: ['chartData', 'options'],
  mounted() {
    this.renderChart(this.chartData, this.options)
  },
  watch: {
    chartData: {
      deep: true,
      handler() {
        this.$data._chart.destroy()
        this.renderChart(this.chartData, this.options)
      },
    },
  },
}

// Componente de gráfico de linha personalizado
const LineChart = {
  extends: Line,
  props: ['chartData', 'options'],
  mounted() {
    this.renderChart(this.chartData, this.options)
  },
  watch: {
    chartData: {
      deep: true,
      handler() {
        this.$data._chart.destroy()
        this.renderChart(this.chartData, this.options)
      },
    },
  },
}

export default {
  components: {
    BCard,
    StaticsCard,
    BRow,
    BCol,
    BTable,
    BLink,
    BBadge,
    BButton,
    BarChart,
    LineChart,
  },
  setup() {
    const {
      dataStaticsFixos,
      ultimosPedidos,
      resumoRapido,
      chartDataVendas,
      chartDataPedidos,
      loading,
    } = useDashboardVendedor()

    return {
      dataStaticsFixos,
      ultimosPedidos,
      resumoRapido,
      chartDataVendas,
      chartDataPedidos,
      loading,
    }
  },
  computed: {
    ...mapGetters({
      dataStaticsCliente: 'appDashboards/GET_STATICS_CLIENTE',
    }),
  },
  data() {
    return {
      // Opções dos gráficos
      chartOptionsVendas: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true,
            },
            gridLines: {
              display: true,
              drawBorder: false,
            },
          }],
          xAxes: [{
            gridLines: {
              display: false,
              drawBorder: false,
            },
          }],
        },
      },
      chartOptionsPedidos: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true,
            },
            gridLines: {
              display: true,
              drawBorder: false,
            },
          }],
          xAxes: [{
            gridLines: {
              display: false,
              drawBorder: false,
            },
          }],
        },
        elements: {
          point: {
            hoverRadius: 6,
          },
        },
      },
      // Campos da tabela
      camposTabela: [
        { key: 'id', label: 'ID' },
        { key: 'cliente', label: 'CLIENTE' },
        { key: 'data', label: 'DATA' },
        { key: 'valor', label: 'VALOR' },
        { key: 'status', label: 'STATUS' },
      ],
      // Dicas de crescimento
      dicasCrescimento: [
        {
          title: 'Cadastre seus produtos',
          text: 'Adicione seu primeiro produto para começar a vender na plataforma.',
          icon: 'BoxIcon',
          color: 'primary',
          link: '/add-produto',
          linkText: 'Começar agora',
        },
        {
          title: 'Programa de afiliados',
          text: 'Configure seu programa de afiliados para aumentar seu alcance.',
          icon: 'UsersIcon',
          color: 'success',
          link: '/gerenciar-afiliacoes',
          linkText: 'Explorar',
        },
        {
          title: 'Solicite seu saque',
          text: 'Retire seus ganhos e comissões de vendas de forma rápida e segura.',
          icon: 'DollarSignIcon',
          color: 'info',
          link: '/saque',
          linkText: 'Solicitar saque',
        },
      ],
    }
  },
  methods: {
    navigateToPedidos(stat) {
      const query = {}
      // Define o filtro baseado no tipo do card
      if (stat.title === 'Total Pendente') {
        query.status = 'pending'
      } else if (stat.title === 'Total Cancelado') {
        query.status = 'canceled'
      } else if (stat.title === 'Total Vendas') {
        query.status = 'paid'
      }
      // Para outros cards, vai sem filtros específicos
      this.$router.push({
        path: '/pedidos',
        query,
      })
    },
  },
}
</script>

<style scoped>
.bg-light-primary {
  background-color: rgba(79, 125, 243, 0.12) !important;
}
.bg-light-success {
  background-color: rgba(40, 199, 111, 0.12) !important;
}
.bg-light-warning {
  background-color: rgba(255, 159, 67, 0.12) !important;
}
.bg-light-danger {
  background-color: rgba(234, 84, 85, 0.12) !important;
}
.bg-light-info {
  background-color: rgba(0, 207, 232, 0.12) !important;
}
.text-primary { color: #7367f0 !important; }
.text-success { color: #28c76f !important; }
.text-warning { color: #ff9f43 !important; }
.text-danger { color: #ea5455 !important; }
.text-info { color: #00cfe8 !important; }
.card-clickable {
  cursor: pointer;
  transition: transform 0.2s ease-in-out;
}
.card-clickable:hover {
  transform: translateY(-2px);
}
</style>